import { Button } from "@/components/ui/baseUi/button"
import { cn } from "@/modules/lib/utils"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import ProgramEmpty from "@images/empty/program_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { ProgramCalendar } from "@modules/programs/components/programCalendar"
import { ProgramHeader } from "@modules/programs/components/programHeader/programHeader"
import { useProgram, usePrograms } from "@modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ProgramFilter } from "@modules/programs/types/programTypes"
import { EmptyView } from "@modules/ui/components/emptyView"
import { Plus } from "lucide-react"
import { Suspense } from "react"

type ProgramDetailContentProps = {
  programId: string
  calendarRange:
    | { startDate: number | undefined; endDate: number | undefined }
    | { startDate: string; endDate: string }
    | undefined
  className?: string
} & React.ComponentProps<"div">

export function ProgramDetailsContent({ programId, calendarRange, className, ...props }: ProgramDetailContentProps) {
  const { data: ownedPrograms, isLoading: loadingOwnedPrograms } = usePrograms({
    programFilter: ProgramFilter.OWNED,
  })
  const { navigateToProgramCreationTypeSelection } = useProgramSidebar()

  const { data: program, isLoading: loading } = useProgram(programId)
  const showEmptyView = !loadingOwnedPrograms && !((ownedPrograms.totalSize ?? 0) > 0 || program)

  const onCreateProgramClick = () => {
    navigateToProgramCreationTypeSelection()
  }

  return (
    <div
      className={cn(
        "flex h-full w-full min-w-245 flex-col gap-2 px-6 py-6 min-[1961px]:w-426 min-[1961px]:px-0 lg:mx-auto",
        className,
      )}
      {...props}
    >
      <ProgramHeader programId={programId} />

      <div className="mt-8 flex h-full w-full flex-col items-center justify-start">
        {showEmptyView ? (
          <div className="flex w-full grow items-center justify-center rounded-4xl bg-neutral-100">
            <EmptyView
              image={{ src: ProgramEmpty }}
              title={word("program.title.empty")}
              description={word("programs.empty.description")}
              flexGrow={false}
              className="bg-surface-background p-6"
              button={
                <Button variant="primary" onClick={onCreateProgramClick}>
                  <Plus />
                  {word("program.form.addProgram.title")}
                </Button>
              }
              paddingType="In_table"
            />
          </div>
        ) : (
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramCalendar programId={programId} loading={loading} calendarRange={calendarRange} />
          </Suspense>
        )}
      </div>
    </div>
  )
}
